import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { environment } from '@environment/environment';
import { Router } from '@angular/router';
import { catchError, map, Observable, of, tap, timeout } from 'rxjs';
import { StellaInfoApiService, StellaInfoDto } from '@bpp-stella/api/stella';
import { StartupTraceUtil } from '@shared/utils/startup-trace.util';
import { FeatureToggleService } from '@bpp-stella/common/state/feature-toggle.service';

@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {
  private readonly stellaInfoApiService = inject(StellaInfoApiService);
  private readonly featureToggleService = inject(FeatureToggleService);
  private readonly router = inject(Router);

  /** LoadingService re-checks with exhaustMap and CapacitorHttp sets no read timeout, so a hung request must end here. */
  private static readonly UPDATE_CHECK_TIMEOUT_MS = 20_000;

  public readonly versionAction: WritableSignal<AppVersionActionEnum | null> = signal(null);

  public readonly appVersion = environment.appVersion;

  public checkIfUpdateIsAvailable(): Observable<boolean> {
    return this.stellaInfoApiService.apiStellaInfoGet()
      .pipe(
        timeout(AppUpdateService.UPDATE_CHECK_TIMEOUT_MS),
        // The same response carries the DB-backed feature toggles (BRO-1088). Applying them
        // here keeps startup at one stella-info request; on failure the app keeps the
        // defaults the service was seeded with.
        tap((stellaInfoDto: StellaInfoDto) => this.featureToggleService.applyRemoteToggles(stellaInfoDto.featureToggles)),
        map(stellaInfoDto => {
          StartupTraceUtil.mark('update-check-done', 'ok');
          if (!stellaInfoDto.latestAppVersion) {
            return false;
          }
          return this.handleVersionAction(this.getAppVersionAction(stellaInfoDto.latestAppVersion));
        }),
        catchError(() => {
          StartupTraceUtil.mark('update-check-done', 'error');
          return of(false);
        })
      );
  }

  public checkForUpdatesAndRedirect(): void {
    this.checkIfUpdateIsAvailable().subscribe(redirect => {
      if (redirect) {
        void this.router.navigate([`/update`]);
      }
    });
  }

  private handleVersionAction(version: AppVersionActionEnum): boolean {
    this.versionAction.set(version);
    if (version === AppVersionActionEnum.UPDATE_MAJOR_VERSION || version === AppVersionActionEnum.UPDATE_MINOR_VERSION) {
      return true;
    } else if (version === AppVersionActionEnum.UPDATE_PATCH_VERSION) {
      //TODO: how to handle patch version
      return false;
    }
    console.log('App up to date');
    return false;

  }

  private getAppVersionAction(latestVersion: string): AppVersionActionEnum {
    const appVersion = this.parseVersion(this.appVersion);
    const latest = this.parseVersion(latestVersion);

    if (appVersion.major < latest.major) {
      return AppVersionActionEnum.UPDATE_MAJOR_VERSION;
    }

    if (appVersion.minor < latest.minor) {
      return AppVersionActionEnum.UPDATE_MINOR_VERSION;
    }

    if (appVersion.patch < latest.patch) {
      return AppVersionActionEnum.UPDATE_PATCH_VERSION;
    }

    return AppVersionActionEnum.UP_TO_DATE;

  }

  private parseVersion(version: string): AppVersion {
    const [major, minor, patch] = version.split('.').map(Number);
    return { major, minor, patch };
  }

}

export enum AppVersionActionEnum {
  UPDATE_MAJOR_VERSION = 'UPDATE_MAJOR_VERSION',
  UPDATE_MINOR_VERSION = 'UPDATE_MINOR_VERSION',
  UPDATE_PATCH_VERSION = 'UPDATE_PATCH_VERSION',
  UP_TO_DATE = 'UP_TO_DATE',
}

interface AppVersion {
  major: number;
  minor: number;
  patch: number;
}
